import { GraphQLClient } from "graphql-request";
import { getApiUrl } from "src/utils";
import { v4Operations } from "./operations";
import {
  AccountBalance,
  AccountsBalancesPayload,
  CreateSessionPayload,
  CreateSessionResponse,
  CreateWithdrawalFiatPayload,
  CreateWithdrawalFiatResponse,
  EstimatePaymentFeePayload,
  OzowEstimateQuotePayload,
  OzowEstimateQuoteResult,
  PaymentLimit,
  PaymentsLimitsPayload,
  CalculatePaymentsLimitsPayload,
  LimitsStatus,
  PaymentsRoutesPayload,
  PaymentsRoutesResponse,
  User,
  UserPayload,
} from "./types";

export enum SPClientEndpoints {
  CREATE_SESSION = "/psp/ozow/session",
  ESTIMATE_QUOTE = "/psp/ozow/estimate-quote",
  RETRIEVE_DATA = "/secure/cookies?cookie_key=",
}

interface IGqlRequest {
  query: string;
  variables?: Record<string, unknown>;
}

class v4Client {
  private client: GraphQLClient;
  private authorization: string;
  private apiUrl: string;

  constructor(apiUrl: string, headers?: Headers) {
    this.apiUrl = apiUrl;
    this.client = new GraphQLClient(apiUrl, { headers });
    this.authorization = headers?.get("Authorization") || headers?.get("authorization") || "";
  }

  /**
   * Set the authorization token for API requests
   */
  public setToken(token?: string): void {
    if (token) {
      this.client.setHeader("Authorization", token);
      this.authorization = token;
    }
  }

  /**
   * Execute a GraphQL request
   */
  private async request<T = any>(request: IGqlRequest): Promise<T> {
    try {
      return await this.client.request<T>({
        document: request.query,
        variables: request.variables,
      });
    } catch (error) {
      console.error("Failed to execute GraphQL request:", error);
      throw error;
    }
  }

  /**
   * Get accounts balances for a user
   */
  public async getAccountsBalances(payload: AccountsBalancesPayload): Promise<AccountBalance[]> {
    const response = await this.request<{ accounts_balances: AccountBalance[] }>(v4Operations.getAccountsBalances(payload));
    return response.accounts_balances;
  }

  /**
   * Get user information including KYC data and limits
   */
  public async getUser(payload: UserPayload): Promise<User> {
    const response = await this.request<{ user: User }>(v4Operations.getUser(payload));
    return response.user;
  }

  /**
   * Estimate the fee for a payment
   */
  public async estimatePaymentFee(payload: EstimatePaymentFeePayload): Promise<number> {
    const response = await this.request<{ estimate_payment_fee: number }>(v4Operations.getEstimatePaymentFee(payload));
    return response?.estimate_payment_fee || 0;
  }

  /**
   * Get payments limits for a given currency and limit group
   */
  public async getPaymentsLimits(payload: PaymentsLimitsPayload): Promise<PaymentLimit[]> {
    const response = await this.request<{ payments_limits: PaymentLimit[] }>(v4Operations.getPaymentsLimits(payload));
    return response.payments_limits;
  }

  /**
   * Live per-period used/limit/remaining (in notion_currency) for the
   * current user's limit group. Backed by the `calculate_payments_limits`
   * mutation. Returns null when the backend yields nothing.
   */
  public async getCalculatedPaymentsLimits(payload: CalculatePaymentsLimitsPayload): Promise<LimitsStatus | null> {
    const response = await this.request<{ calculate_payments_limits: LimitsStatus }>(
      v4Operations.calculatePaymentsLimits(payload)
    );
    return response.calculate_payments_limits ?? null;
  }

  /**
   * Create a withdrawal fiat payment
   */
  public async createWithdrawalFiat(payload: CreateWithdrawalFiatPayload): Promise<CreateWithdrawalFiatResponse> {
    const response = await this.request<{ create_withdrawal_fiat: CreateWithdrawalFiatResponse }>(
      v4Operations.createWithdrawalFiat(payload)
    );
    return response.create_withdrawal_fiat;
  }

  /**
   * Get payments routes for a given payment route ID
   */
  public async getPaymentsRoutes(payload: PaymentsRoutesPayload): Promise<PaymentsRoutesResponse[]> {
    const response = await this.request<{ payments_routes: PaymentsRoutesResponse[] }>(v4Operations.getPaymentsRoutes(payload));
    return response.payments_routes;
  }

  /**
   * Ozow instant-buy estimate quote (REST endpoint)
   */
  public async getOzowEstimateQuote(payload: OzowEstimateQuotePayload): Promise<OzowEstimateQuoteResult> {
    try {
      const params = new URLSearchParams({
        payment_route_id: payload.payment_route_id,
        amount: String(payload.amount),
      });
      const response = await fetch(
        `${this.apiUrl.replace("/graphql", "")}${SPClientEndpoints.ESTIMATE_QUOTE}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: this.authorization,
          },
        }
      );

      if (!response.ok) {
        const errorBody = await response.text();
        let parsed: { message?: string; extensions?: { code?: string }; statusCode?: number } = {};

        try {
          parsed = JSON.parse(errorBody);
        } catch {
          // Response body is not JSON — fall back to raw text below
        }

        const error = new Error(
          parsed.message || `Failed to get Ozow estimate quote (${response.status})`
        ) as Error & { code?: string; statusCode?: number };
        error.code = parsed.extensions?.code;
        error.statusCode = parsed.statusCode ?? response.status;
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to get estimate quote:", error);
      throw error;
    }
  }

  /**
   * Create a payment session (REST endpoint)
   */
  public async createSession(payload: CreateSessionPayload): Promise<CreateSessionResponse> {
    try {
      const response = await fetch(`${this.apiUrl.replace("/graphql", "")}${SPClientEndpoints.CREATE_SESSION}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: this.authorization,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.warn(`Failed to create session: ${response.status} ${response.statusText}. ${errorBody}`);
        return {};
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to create session:", error);
      return {};
    }
  }

  /**
   * Retrieve secure data from cookies (REST endpoint)
   */
  public async retrieveData(key: string): Promise<Record<string, any>> {
    try {
      const response = await fetch(
        `${this.apiUrl.replace("/graphql", "")}${SPClientEndpoints.RETRIEVE_DATA}${encodeURIComponent(key)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorBody = await response.text();
        console.warn(`Failed to retrieve secure data: ${response.status} ${response.statusText}. ${errorBody}`);
        return {};
      }

      const responseData = await response.json();
      return responseData.cookie;
    } catch (error) {
      console.error("Failed to retrieve secure data:", error);
      return {};
    }
  }
}

export const v4Service = new v4Client(getApiUrl() || "");
